/**
 * Cart Gift Manager — « Emballage cadeau »
 * ----------------------------------------
 * Aligne le panier sur le choix d'emballage résolu côté Liquid (`#cart-gift-state`).
 * Objectif : très peu de requêtes, à la manière de l'échantillon gratuit.
 *
 * Principes anti-requêtes :
 *  - Réconciliation GROUPÉE : toutes les mutations nécessaires (retraits + ajouts) sont
 *    calculées d'un coup et envoyées en 1 à 2 requêtes, suivies d'UN SEUL `cart:update`.
 *  - On IGNORE nos propres `cart:update` (sourceId `gift-manager`) → aucune ré-entrée / boucle.
 *  - Debounce pour coalescer les rafales d'événements.
 *  - `busy` (une requête en vol), variantes en échec neutralisées, et disjoncteur de sécurité.
 *
 * Voir snippets/cart-gift-manager.liquid pour la logique métier.
 */
(function () {
  if (window.__cartGiftManagerInit) return;
  window.__cartGiftManagerInit = true;

  var MAX_ACTIONS = 6; // disjoncteur : passes de mutation max par fenêtre
  var WINDOW = 5000; // ms
  var DEBOUNCE = 200; // ms

  var busy = false;
  var failedVariants = {};
  var actionTimes = [];
  var tripped = false;
  var debounceTimer = null;

  /** Blob brut (toujours dispo, même si l'emballage cadeau est désactivé). */
  function getBlob() {
    var el = document.getElementById('cart-gift-state');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /** État emballage cadeau (null si désactivé). */
  function getState() {
    var b = getBlob();
    return b && b.enabled ? b : null;
  }

  function getSectionId() {
    var host = document.querySelector('cart-items-component[data-section-id]');
    return host ? host.dataset.sectionId : '';
  }

  function circuitTripped() {
    if (tripped) return true;
    var now = Date.now();
    actionTimes = actionTimes.filter(function (t) { return now - t < WINDOW; });
    if (actionTimes.length >= MAX_ACTIONS) {
      tripped = true;
      // eslint-disable-next-line no-console
      console.warn('[cart-gift-manager] Trop de mutations — réconciliation stoppée (évite un 429).');
      return true;
    }
    return false;
  }

  function isErrorResponse(data) {
    return !!(data && (data.status || data.description || data.errors));
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json().catch(function () { return {}; }); });
  }

  function dispatchUpdate(sections) {
    /** @type {{ sections: any, itemCount?: number }} */
    var data = { sections: sections || {} };

    // Récupère le compteur panier (hors packaging) depuis le HTML de section re-rendu,
    // pour que la bulle du header (cart-icon) reste juste après un ajout/retrait cadeau.
    var sectionId = getSectionId();
    var html = sections && sectionId ? sections[sectionId] : null;
    if (html) {
      try {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var countEl = doc.querySelector('[ref="cartItemCount"]');
        if (countEl) {
          var count = parseInt(countEl.textContent, 10);
          if (!isNaN(count)) data.itemCount = count;
        }
      } catch (e) { /* no-op */ }
    }

    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { resource: null, sourceId: 'gift-manager', data: data }
    }));
  }

  /**
   * Applique en une passe : retraits (par line key) puis ajouts (par variante + propriétés),
   * avec au plus 1 `cart:update` à la fin. `attributes` optionnel (ex: `_gift_choice`).
   *
   * @param {string[]} removeKeys - line keys à retirer
   * @param {{id:number, properties:object}[]} addItems - variantes à ajouter
   * @param {object} [attributes] - attributs de panier à écrire
   */
  function apply(removeKeys, addItems, attributes, note) {
    if (busy || circuitTripped()) return;

    removeKeys = (removeKeys || []).filter(Boolean);
    addItems = (addItems || []).filter(function (it) { return !failedVariants[it.id]; });

    var hasRemoves = removeKeys.length > 0;
    var hasAdds = addItems.length > 0;
    var hasNote = typeof note === 'string';
    if (!hasRemoves && !hasAdds && !attributes && !hasNote) return;

    var sectionId = getSectionId();
    busy = true;
    actionTimes.push(Date.now());

    var chain = Promise.resolve(null);

    if (hasRemoves || attributes || hasNote) {
      var updateBody = {};
      if (hasRemoves) {
        var updates = {};
        removeKeys.forEach(function (k) { updates[k] = 0; });
        updateBody.updates = updates;
      }
      if (attributes) updateBody.attributes = attributes;
      if (hasNote) updateBody.note = note;
      // sections demandées ici seulement si c'est la dernière opération
      if (!hasAdds && sectionId) updateBody.sections = sectionId;
      chain = chain.then(function () { return postJSON('/cart/update.js', updateBody); });
    }

    if (hasAdds) {
      chain = chain.then(function () {
        var addBody = { items: addItems };
        if (sectionId) addBody.sections = sectionId;
        return postJSON('/cart/add.js', addBody).then(function (data) {
          if (isErrorResponse(data)) {
            // eslint-disable-next-line no-console
            console.warn('[cart-gift-manager] Échec ajout produit cadeau —', data.description || data.message, '| réponse:', data, '| items:', addItems);
            addItems.forEach(function (it) { failedVariants[it.id] = true; });
          }
          return data;
        });
      });
    }

    chain
      .then(function (data) {
        busy = false;
        // On ne redispatche (donc re-render) que si la requête a réellement abouti
        // (sections présentes). Évite un rendu inutile en cas d'échec d'ajout.
        if (data && data.sections) dispatchUpdate(data.sections);
      })
      .catch(function () {
        busy = false;
      });
  }

  /**
   * Vide entièrement le panier s'il ne reste que des cadeaux/échantillons (aucun vrai produit).
   * Piloté par le flag `cartShouldClear` calculé côté Liquid. Fonctionne même si l'emballage
   * cadeau est désactivé (lit le blob brut).
   * @returns {boolean} true si un vidage a été déclenché.
   */
  function clearCartIfEmptyOfProducts() {
    if (busy || circuitTripped()) return false;
    var b = getBlob();
    if (!b || !b.cartShouldClear) return false;

    var sectionId = getSectionId();
    busy = true;
    actionTimes.push(Date.now());

    /** @type {Record<string, any>} */
    var body = {};
    if (sectionId) body.sections = sectionId;

    postJSON('/cart/clear.js', body)
      .then(function (data) {
        busy = false;
        dispatchUpdate((data && data.sections) || {});
      })
      .catch(function () { busy = false; });
    return true;
  }

  /**
   * Réconciliation automatique : aligne les lignes cadeaux sur wantPochon / wantCoffret.
   * N'émet une requête que s'il y a une réelle divergence.
   */
  function reconcileNow() {
    if (busy || tripped) return;
    // Si le panier ne contient plus que des cadeaux/échantillons → on le vide entièrement.
    if (clearCartIfEmptyOfProducts()) return;
    var s = getState();
    if (!s) return;

    var removeKeys = [];
    var addItems = [];
    var clearNote = false;

    // Retraits : présent mais non désiré
    if (s.pochonPresent && !s.wantPochon) removeKeys.push(s.pochonKey);
    if (s.coffretPresent && !s.wantCoffret) removeKeys.push(s.coffretKey);
    if (s.rubanPresent && !s.wantCoffret) removeKeys.push(s.rubanKey);
    // La carte personnalisée est liée à la boîte cadeau : retirée si plus de coffret.
    if (s.cartePresent && !s.wantCoffret) { removeKeys.push(s.carteKey); clearNote = true; }

    // Ajouts : désiré mais absent
    if (s.wantPochon && !s.pochonPresent) {
      addItems.push({ id: s.pochonVariantId, quantity: 1, properties: { _is_pochon: '1' } });
    }
    if (s.wantCoffret && !s.coffretPresent) {
      addItems.push({ id: s.coffretVariantId, quantity: 1, properties: { _is_coffret: '1' } });
    }
    if (s.wantCoffret && !s.rubanPresent) {
      addItems.push({ id: s.rubanVariantId, quantity: 1, properties: { _is_ruban: '1' } });
    }

    apply(removeKeys, addItems, clearNote ? { MessagePerso: '' } : undefined, clearNote ? '' : undefined);
  }

  function reconcile() {
    if (tripped) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcileNow, DEBOUNCE);
  }

  // --- Choix utilisateur (boutons radio) : mutation immédiate + persistance du choix ---
  document.addEventListener('change', function (e) {
    if (busy || tripped) return;
    var target = e.target;
    if (!target || target.name !== 'cart-gift-choice' || !target.checked) return;

    var s = getState();
    if (!s) return;

    var choice = target.value; // 'pochon' | 'coffret' | 'none'
    var wantPochon = choice === 'pochon';
    var wantCoffret = choice === 'coffret';

    var removeKeys = [];
    var addItems = [];
    var clearNote = false;

    if (s.pochonPresent && !wantPochon) removeKeys.push(s.pochonKey);
    if (s.coffretPresent && !wantCoffret) removeKeys.push(s.coffretKey);
    if (s.rubanPresent && !wantCoffret) removeKeys.push(s.rubanKey);
    // Quitter la boîte cadeau retire aussi la carte personnalisée + son message.
    if (s.cartePresent && !wantCoffret) { removeKeys.push(s.carteKey); clearNote = true; }

    if (wantPochon && !s.pochonPresent) {
      addItems.push({ id: s.pochonVariantId, quantity: 1, properties: { _is_pochon: '1' } });
    }
    if (wantCoffret && !s.coffretPresent) {
      addItems.push({ id: s.coffretVariantId, quantity: 1, properties: { _is_coffret: '1' } });
    }
    if (wantCoffret && !s.rubanPresent) {
      addItems.push({ id: s.rubanVariantId, quantity: 1, properties: { _is_ruban: '1' } });
    }

    var giftAttrs = { _gift_choice: choice };
    if (clearNote) giftAttrs.MessagePerso = '';
    apply(removeKeys, addItems, giftAttrs, clearNote ? '' : undefined);
  });

  // --- Carte personnalisée → note de commande (cart.note) ET note_attribute `MessagePerso` (lue par le logisticien LogVAD/EffiTrace) ---
  // Requête légère, sans sections ni cart:update (le champ n'affecte pas l'affichage du panier).
  function saveNote(value) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: value, attributes: { MessagePerso: value } })
    }).catch(function () {});
  }

  function updateCounter() {
    var ta = document.getElementById('cart-gift-card-message');
    var counter = document.getElementById('cart-gift-card-counter');
    if (ta && counter) counter.textContent = ta.value.length + '/150';
  }

  /**
   * Ajoute/retire le produit « carte personnalisée » (variant carteVariantId).
   * Ajout : /cart/add.js (+ /cart/update.js pour enregistrer le message si fourni).
   * Retrait : /cart/update.js avec vidage de la note. Un seul cart:update à la fin.
   *
   * @param {boolean} add - true = ajoute la carte, false = la retire.
   * @param {string} [noteValue] - message à enregistrer dans cart.note lors de l'ajout.
   */
  function applyCarte(add, noteValue) {
    if (busy || circuitTripped()) return;
    var b = getBlob();
    if (!b || !b.carteVariantId) return;

    var sectionId = getSectionId();
    var hasNote = typeof noteValue === 'string';
    busy = true;
    actionTimes.push(Date.now());

    /** @type {Promise<any>} */
    var chain = Promise.resolve(null);

    if (add) {
      if (failedVariants[b.carteVariantId]) { busy = false; return; }
      chain = chain.then(function () {
        /** @type {Record<string, any>} */
        var addBody = { items: [{ id: b.carteVariantId, quantity: 1, properties: { _is_carte: '1' } }] };
        if (!hasNote && sectionId) addBody.sections = sectionId; // sections sur la dernière op
        return postJSON('/cart/add.js', addBody).then(function (data) {
          if (isErrorResponse(data)) {
            // eslint-disable-next-line no-console
            console.warn('[cart-gift-manager] Échec ajout carte personnalisée —', data.description || data.message, '| réponse:', data);
            failedVariants[b.carteVariantId] = true;
          }
          return data;
        });
      });
      if (hasNote) {
        chain = chain.then(function () {
          /** @type {Record<string, any>} */
          var noteBody = { note: noteValue, attributes: { MessagePerso: noteValue } };
          if (sectionId) noteBody.sections = sectionId;
          return postJSON('/cart/update.js', noteBody);
        });
      }
    } else {
      chain = chain.then(function () {
        /** @type {Record<string, any>} */
        var updateBody = { note: '', attributes: { MessagePerso: '' } };
        if (b.carteKey) updateBody.updates = { [b.carteKey]: 0 };
        if (sectionId) updateBody.sections = sectionId;
        return postJSON('/cart/update.js', updateBody);
      });
    }

    chain
      .then(function (data) {
        busy = false;
        if (data && data.sections) dispatchUpdate(data.sections);
      })
      .catch(function () { busy = false; });
  }

  document.addEventListener('change', function (e) {
    /** @type {any} */
    var t = e.target;
    if (!t) return;

    if (t.id === 'cart-gift-card-toggle') {
      var field = document.querySelector('.cart-gift-card__field');
      var ta = document.getElementById('cart-gift-card-message');
      if (t.checked) {
        // On affiche juste la zone de saisie. Le produit carte n'est ajouté que
        // lorsqu'un message non vide est saisi (voir handler du textarea ci-dessous).
        if (field) field.hidden = false;
        if (ta) ta.focus();
      } else {
        if (field) field.hidden = true;
        if (ta) { ta.value = ''; updateCounter(); }
        applyCarte(false); // retire le produit (s'il existe) + vide la note
      }
      return;
    }

    if (t.id === 'cart-gift-card-message') {
      var msg = t.value.trim();
      var state = getBlob();
      if (msg.length > 0) {
        if (state && !state.cartePresent) {
          applyCarte(true, t.value); // 1er message → ajoute le produit + enregistre la note
        } else {
          saveNote(t.value); // produit déjà présent → simple mise à jour de la note
        }
      } else if (state && state.cartePresent) {
        applyCarte(false); // message vidé → retire le produit + vide la note
      } else {
        saveNote(''); // rien à retirer
      }
    }
  });

  document.addEventListener('input', function (e) {
    if (e.target && e.target.id === 'cart-gift-card-message') updateCounter();
  });

  // --- Réconciliation auto : on ignore nos propres événements (pas de boucle) ---
  document.addEventListener('cart:update', function (e) {
    updateCounter();
    if (e.detail && e.detail.sourceId === 'gift-manager') return;
    reconcile();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reconcile(); updateCounter(); });
  } else {
    reconcile();
    updateCounter();
  }
})();
